import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { changePasswordRequest, getOrdersRequest, updateAccountRequest } from "../components/authApi";
import SeoManager from "../SeoManager";

export default function AccountPage() {
  const { user, loading, updateUser } = useAuth();
  const [form, setForm] = useState({
    username: "",
    full_name: "",
    contact_number: "",
    email: "",
  });
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [passData, setPassData] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [passMsg, setPassMsg] = useState("");

  useEffect(() => {
    if (!user) {
      setForm({ username: "", full_name: "", contact_number: "", email: "" });
      return;
    }

    setForm({
      username: user.username || "",
      full_name: user.full_name || "",
      contact_number: user.contact_number || "",
      email: user.email || "",
    });
  }, [user]);

  useEffect(() => {
    if (!user) {
      setOrders([]);
      setOrdersLoading(false);
      return;
    }

    const fetchOrders = async () => {
      setOrdersLoading(true);
      try {
        const data = await getOrdersRequest();
        setOrders(data.status === "success" && Array.isArray(data.data) ? data.data : []);
      } catch (error) {
        console.error("Error fetching orders:", error);
        setOrders([]);
      } finally {
        setOrdersLoading(false);
      }
    };

    fetchOrders();
  }, [user]);

  const handleChange = (event) => {
    setForm((prev) => ({ ...prev, [event.target.name]: event.target.value }));
  };

  const handlePassChange = (event) => {
    setPassData((prev) => ({ ...prev, [event.target.name]: event.target.value }));
  };

  const handleUpdate = async (event) => {
    event.preventDefault();
    setMessage("");
    try {
      const response = await updateAccountRequest(form);
      setMessage(response.message || "Profile updated.");
      if (response.status === "success" && response.data) {
        updateUser(response.data);
      }
    } catch (error) {
      console.error("Account update error:", error);
      setMessage("Failed to update account.");
    }
  };

  const handleChangePassword = async (event) => {
    event.preventDefault();
    setPassMsg("");
    try {
      const response = await changePasswordRequest(passData);
      setPassMsg(response.message || "Password updated.");
      if (response.status === "success") {
        setPassData({ current_password: "", new_password: "", confirm_password: "" });
      }
    } catch (error) {
      console.error("Password change error:", error);
      setPassMsg("Failed to change password.");
    }
  };

  if (loading) {
    return <p>Loading account details...</p>;
  }

  if (!user?.username) {
    return (
      <div className="content-page">
        <SeoManager
          title="My Account"
          description="Manage your Asher Jewels PH account."
          path="/account"
          robots="noindex,nofollow"
        />
        <section className="content-hero">
          <p className="category-section__eyebrow">Customer Account</p>
          <h1>My Account</h1>
          <p>Sign in to manage your account details, saved delivery information, and purchase history.</p>
        </section>
        <div className="content-card content-empty">
          <h2>Login required</h2>
          <p>You need to sign in before you can access your saved account information and purchase records.</p>
          <Link to="/login" className="content-link-button">Go to Login</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="content-page">
      <SeoManager
        title="My Account"
        description="Manage your Asher Jewels PH account details, password, and purchase history."
        path="/account"
        robots="noindex,nofollow"
      />
      <section className="content-hero">
        <p className="category-section__eyebrow">Customer Account</p>
        <h1>My Account</h1>
        <p>
          Update your profile, manage your password, and review your purchase history in one secure place.
        </p>
      </section>

      <section className="account-summary-grid">
        <article className="content-side-card">
          <h3>Account Snapshot</h3>
          <p><strong>Name:</strong> {form.full_name}</p>
          <p><strong>Username:</strong> {form.username}</p>
          <p><strong>Contact:</strong> {form.contact_number}</p>
          <p><strong>Email:</strong> {form.email}</p>
          <p><strong>Purchase Records:</strong> {orders.length}</p>
        </article>

        <article className="content-side-card">
          <h3>Quick Access</h3>
          <p>Jump back to your saved items or review your completed orders.</p>
          <div className="content-link-group">
            <Link to="/wishlist" className="content-link-button">My Wishlist</Link>
            <Link to="/orders" className="content-link-button is-secondary">My Orders</Link>
          </div>
        </article>
      </section>

      <section className="content-layout">
        <div className="content-main">
          <article className="content-card">
            <h2>Profile Information</h2>
            <form onSubmit={handleUpdate} className="account-form">
              <label>
                Username
                <input type="text" value={form.username} disabled />
              </label>
              <label>
                Full Name
                <input name="full_name" value={form.full_name} onChange={handleChange} required />
              </label>
              <label>
                Contact Number
                <input name="contact_number" value={form.contact_number} onChange={handleChange} required />
              </label>
              <label>
                Email
                <input name="email" value={form.email} onChange={handleChange} type="email" required />
              </label>
              <button type="submit" className="gold-button">Save Changes</button>
              {message && <p className="feedback">{message}</p>}
            </form>
          </article>

          <article className="content-card">
            <h2>Change Password</h2>
            <form onSubmit={handleChangePassword} className="account-form">
              <label>
                Current Password
                <input type="password" name="current_password" value={passData.current_password} onChange={handlePassChange} required />
              </label>
              <label>
                New Password
                <input type="password" name="new_password" value={passData.new_password} onChange={handlePassChange} required />
              </label>
              <label>
                Confirm New Password
                <input type="password" name="confirm_password" value={passData.confirm_password} onChange={handlePassChange} required />
              </label>
              <button type="submit" className="gold-button">Change Password</button>
              {passMsg && <p className="feedback">{passMsg}</p>}
            </form>
          </article>

          <article className="content-card">
            <h2>Purchase History</h2>
            {ordersLoading ? (
              <p>Loading your purchase history...</p>
            ) : orders.length === 0 ? (
              <p>No completed purchases yet.</p>
            ) : (
              <div className="responsive-table">
                <table className="orders-table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Code</th>
                      <th>Price</th>
                      <th>Status</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order) => (
                      <tr key={order.order_id}>
                        <td>{order.product_name}</td>
                        <td>{order.product_code}</td>
                        <td>PHP {Number(order.pricePHP).toLocaleString()}</td>
                        <td>{order.status}</td>
                        <td>{new Date(order.order_date).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </article>
        </div>

        <aside className="content-side-card">
          <h3>Order Notes</h3>
          <p>Shipping lead time is usually 1 to 3 days for available items after payment confirmation.</p>
          <p>Order records shown here are tied to your logged-in account only.</p>
          <p>If you need assistance with a previous purchase, please contact customer service with your order code.</p>
        </aside>
      </section>
    </div>
  );
}
